/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * NestedRelatedBlocks — the related tables of a related table.
 *
 * The resolver has always accepted `children` inside a source, so an event can
 * show each activity's route and venue. Until this existed the panel could only
 * edit the first level, and anything deeper was written through the API and
 * then invisible here — which is how a rebuilt application silently lost it.
 *
 * TWO PARTS, because the panel is narrow:
 *
 *   - Under a ticked table, one line: the nested blocks already chosen, as
 *     chips, and a button. Nothing is indented, so nothing is squeezed.
 *   - The editing happens in a dialog, one level at a time, with a path at the
 *     top (categories › route) instead of boxes inside boxes.
 *
 * The dialog edits a DRAFT of the whole subtree. Nothing reaches the stored
 * configuration until Apply, so Cancel, Escape and a click outside genuinely
 * discard — the same contract as ConditionRuleDialog.
 *
 * Nothing about any schema is assumed. What is offered at a level is exactly
 * what the related-edges endpoint reports for that level's own table, fetched
 * through `loadChildEdges(tableName)`. The stored shape is the one the server
 * already reads: a `children` list on the parent, each entry `{table, via, dir,
 * as, fields}` like a top-level edge.
 *
 * The parent does NOT need to expose the column a child matches on — the
 * server reads join columns itself and strips them again (see _select_for and
 * _project in public_data_sources.py) — so the parent's own columns are never
 * touched from here.
 *
 * Stored children the endpoint no longer offers (metadata changed under a
 * saved config) are kept exactly where they are: every edit starts from the
 * stored list and changes only the entry being edited.
 */

import React from 'react';

import { useDialogInset } from './dialogInset';

// Mirrors MAX_DEPTH in public_data_sources.py. Top-level blocks are level 1;
// the server refuses a config nested deeper than this, so nothing deeper is
// offered.
export const MAX_NESTING_DEPTH = 4;

function keyOf(block) {
  return block.as || block.table;
}

// Keeps the stored shape tidy: a parent whose last child was removed goes back
// to having no `children` key at all, exactly as before anything was nested.
export function withChildren(block, children) {
  const { children: _previous, ...rest } = block;
  return children.length ? { ...rest, children } : rest;
}

// A level inside the draft is addressed by the child tables walked to reach it.
function nodeAt(list, path) {
  let node = null;
  let current = list;
  for (const table of path) {
    node = current.find(c => c.table === table);
    if (!node) return null;
    current = node.children || [];
  }
  return node;
}

function setChildrenAt(list, path, next) {
  if (path.length === 0) return next;
  const [table, ...rest] = path;
  return list.map(c => (
    c.table === table ? withChildren(c, setChildrenAt(c.children || [], rest, next)) : c
  ));
}

// The edge straight back to the table a block hangs off would only fetch that
// record again, so it is not offered.
function offeredEdges(edges, block, from) {
  return edges.filter(edge => !(
    edge.table === from && edge.via === block.via && edge.dir !== block.dir
  ));
}

function useChildEdges(table, loadChildEdges) {
  const [state, setState] = React.useState({ status: 'loading', edges: [] });

  React.useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', edges: [] });
    Promise.resolve()
      .then(() => loadChildEdges(table))
      .then(edges => {
        if (!cancelled) setState({ status: 'ok', edges: Array.isArray(edges) ? edges : [] });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', edges: [] });
      });
    return () => { cancelled = true; };
  }, [table, loadChildEdges]);

  return state;
}

function countNested(block) {
  return (block.children || []).reduce((n, c) => n + 1 + countNested(c), 0);
}

// ─────────────────────────────────────────────────────────────────────────
// Summary — the one line under a ticked table
// ─────────────────────────────────────────────────────────────────────────

export default function NestedRelatedBlocks({
  parent, parentFrom, path, level, loadChildEdges, onChange,
}) {
  const state = useChildEdges(parent.table, loadChildEdges);
  const [open, setOpen] = React.useState(false);

  if (state.status === 'loading') return null;

  if (state.status === 'error') {
    return (
      <div className="form-text text-danger mt-2">
        Couldn't load the related tables of <code>{parent.table}</code>. Reload the page to
        try again — nothing nested under <code>{keyOf(parent)}</code> was changed.
      </div>
    );
  }

  const stored = parent.children || [];
  const offered = offeredEdges(state.edges, parent, parentFrom);
  if (offered.length === 0 && stored.length === 0) return null;

  return (
    <div
      data-testid={`nested-${path}`}
      className="mt-2 pt-2 d-flex flex-wrap align-items-center gap-1"
      style={{ borderTop: '1px dashed #e2e8f0' }}
    >
      <span className="small fw-semibold me-1">Related tables:</span>
      {stored.length === 0 && <span className="small text-muted">none</span>}
      {stored.map(child => {
        const empty = (child.fields || []).length === 0;
        const deeper = countNested(child);
        return (
          <span
            key={child.table}
            className="badge rounded-pill border fw-normal text-break"
            style={{ background: empty ? '#fef2f2' : '#f5f3ff',
                     color: empty ? '#b91c1c' : '#4e2e80',
                     borderColor: empty ? '#fecaca' : '#ddd6fe' }}
          >
            <span>{keyOf(child)}</span>
            {deeper > 0 && <span className="ms-1 text-muted">+{deeper}</span>}
            {empty && <span className="ms-1">· no columns</span>}
          </span>
        );
      })}
      <button
        type="button"
        className="btn btn-sm btn-outline-primary ms-auto"
        onClick={() => setOpen(true)}
      >
        {stored.length ? 'Edit related tables' : 'Add related tables'}
      </button>

      {open && (
        <NestedRelatedDialog
          root={parent}
          rootFrom={parentFrom}
          rootPath={path}
          rootLevel={level}
          loadChildEdges={loadChildEdges}
          onApply={children => {
            setOpen(false);
            // Apply without a change does not mark the page as edited.
            if (JSON.stringify(children) !== JSON.stringify(stored)) onChange(children);
          }}
          onCancel={() => setOpen(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Dialog — edits one level at a time, over a draft of the whole subtree
// ─────────────────────────────────────────────────────────────────────────

function NestedRelatedDialog({
  root, rootFrom, rootPath, rootLevel, loadChildEdges, onApply, onCancel,
}) {
  const [draft, setDraft] = React.useState(() => root.children || []);
  // Child tables walked from the root to the level being edited.
  const [path, setPath] = React.useState([]);
  const dialogRef = React.useRef(null);
  const inset = useDialogInset();

  React.useEffect(() => {
    if (dialogRef.current) dialogRef.current.focus();
    function onKeyDown(e) { if (e.key === 'Escape') onCancel(); }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  // The block whose children this level edits, and the table it hangs off.
  const block = path.length ? nodeAt(draft, path) : { ...root, children: draft };
  const from = path.length === 0 ? rootFrom
    : path.length === 1 ? root.table
    : nodeAt(draft, path.slice(0, -1)).table;
  const level = rootLevel + path.length;

  const trail = [keyOf(root)];
  for (let i = 1; i <= path.length; i += 1) trail.push(keyOf(nodeAt(draft, path.slice(0, i))));
  const pathKey = [rootPath, ...trail.slice(1)].join('__');

  const state = useChildEdges(block.table, loadChildEdges);
  const offered = offeredEdges(state.edges, block, from);
  const stored = block.children || [];
  const byTable = Object.fromEntries(stored.map(c => [c.table, c]));
  const kept = stored.filter(c => !offered.some(e => e.table === c.table));

  function write(next) {
    setDraft(d => setChildrenAt(d, path, next));
  }

  function toggle(edge, on) {
    if (on) {
      write([...stored, {
        table: edge.table,
        via: edge.via,
        dir: edge.dir,
        as: edge.suggested_alias || edge.table,
        // Deliberately empty, as at the top level: exposure is opt-in.
        fields: [],
      }]);
    } else {
      write(stored.filter(c => c.table !== edge.table));
    }
  }

  function patch(table, change) {
    write(stored.map(c => (c.table === table ? { ...c, ...change } : c)));
  }

  function toggleField(table, fieldName, on) {
    const current = byTable[table]?.fields || [];
    patch(table, {
      fields: on ? [...current, fieldName] : current.filter(f => f !== fieldName),
    });
  }

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center
                 justify-content-center"
      style={{ background: 'rgba(24,22,44,.42)', zIndex: 1080, overflowY: 'auto',
               // Inline padding, not Bootstrap's `p-3`, whose `!important` would
               // beat the inset. Centre on the work area — see dialogInset.
               padding: '1rem',
               paddingLeft: `calc(1rem + ${inset}px)` }}
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="bg-white rounded-3 shadow-lg d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-label={`Related tables of ${keyOf(root)}`}
        tabIndex={-1}
        ref={dialogRef}
        style={{ maxWidth: 680, width: '100%', maxHeight: '86vh' }}
      >
        <div className="px-3 py-3 border-bottom d-flex justify-content-between align-items-start">
          <div className="me-3" style={{ minWidth: 0 }}>
            <div className="small text-muted">Related tables of</div>
            <div className="fw-bold text-break" data-testid="nested-path">
              {trail.map((name, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-muted mx-1">›</span>}
                  {i < trail.length - 1 ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline fw-bold text-decoration-none"
                      onClick={() => setPath(path.slice(0, i))}
                    >
                      {name}
                    </button>
                  ) : (
                    <span>{name}</span>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="small text-muted mt-1">
              Each <code>{trail[trail.length - 1]}</code> row gets its own matching rows from
              the tables ticked here.
            </div>
          </div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onCancel} />
        </div>

        <div className="px-3 py-3 overflow-auto">
          {state.status === 'loading' && (
            <div className="small text-muted">Loading related tables…</div>
          )}
          {state.status === 'error' && (
            <div className="small text-danger">
              Couldn't load the related tables of <code>{block.table}</code>.
            </div>
          )}
          {state.status === 'ok' && offered.length === 0 && (
            <div className="small text-muted">
              <code>{block.table}</code> has no related tables to add.
            </div>
          )}

          {offered.map(edge => {
            const chosen = byTable[edge.table];
            const isOn = Boolean(chosen);
            const id = `${pathKey}-${edge.table}`;
            const childLevel = level + 1;
            const deeper = chosen ? countNested(chosen) : 0;

            return (
              <div
                key={edge.table}
                data-testid={`nested-edge-${id}`}
                className="mb-2 p-2 border rounded"
                style={{ background: isOn ? '#fbfaff' : '#fff', borderColor: '#e2e8f0' }}
              >
                <div className="d-flex align-items-start gap-2">
                  <div className="form-check flex-grow-1" style={{ minWidth: 0 }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`nested-check-${id}`}
                      checked={isOn}
                      disabled={!edge.eligible}
                      onChange={e => toggle(edge, e.target.checked)}
                    />
                    <label
                      className="form-check-label small fw-semibold text-break"
                      htmlFor={`nested-check-${id}`}
                    >
                      {edge.display_name || edge.table}
                      <span className="text-muted fw-normal">
                        {' '}— {edge.dir === 'up' ? 'linked via' : 'matched on'} <code>{edge.via}</code>
                      </span>
                    </label>
                  </div>
                  {isOn && childLevel < MAX_NESTING_DEPTH && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary flex-shrink-0"
                      aria-label={`Related tables of ${keyOf(chosen)}`}
                      onClick={() => setPath([...path, edge.table])}
                    >
                      Related tables{deeper > 0 ? ` (${deeper})` : ''} ›
                    </button>
                  )}
                </div>

                {!edge.eligible && (
                  <div className="form-text text-danger ms-4">
                    {edge.reason} Mark it Public to use it here.
                  </div>
                )}

                {isOn && (
                  <div className="ms-4 mt-2">
                    <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
                      <div>
                        <label className="form-label small fw-semibold mb-1" htmlFor={`nested-alias-${id}`}>
                          Name in template
                        </label>
                        <input
                          id={`nested-alias-${id}`}
                          className="form-control form-control-sm"
                          style={{ maxWidth: 220 }}
                          value={chosen.as || ''}
                          onChange={e => patch(edge.table, { as: e.target.value })}
                        />
                      </div>
                      <div className="form-check mb-1">
                        <input
                          className="form-check-input" type="checkbox"
                          id={`nested-single-${id}`}
                          checked={Boolean(chosen.single)}
                          onChange={e => patch(edge.table, { single: e.target.checked })}
                        />
                        <label className="form-check-label small" htmlFor={`nested-single-${id}`}>
                          Single record (not a list)
                        </label>
                      </div>
                    </div>

                    <div className="small fw-semibold mb-1">Columns to expose</div>
                    <div
                      className="mb-2"
                      style={{ display: 'grid', gap: '4px 12px',
                               gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}
                    >
                      {(edge.fields || []).map(field => {
                        const fieldId = `nested-field-${id}-${field.name}`;
                        return (
                          <div className="form-check" key={field.name} style={{ minWidth: 0 }}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={fieldId}
                              checked={(chosen.fields || []).includes(field.name)}
                              onChange={e => toggleField(edge.table, field.name, e.target.checked)}
                            />
                            <label className="form-check-label small text-break" htmlFor={fieldId}>
                              {field.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>

                    {(chosen.fields || []).length === 0 && (
                      <div className="form-text text-danger">
                        Choose at least one column — this table currently exposes nothing.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {state.status === 'ok' && kept.length > 0 && (
            <div className="form-text">
              Kept as they are (not offered for <code>{block.table}</code> any more):{' '}
              {kept.map(c => <code key={c.table} className="me-1">{keyOf(c)}</code>)}
            </div>
          )}
        </div>

        <div className="px-3 py-2 border-top d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-sm btn-light" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => onApply(draft)}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
